import { Brain, ShieldCheck, Stethoscope, Hammer, MessagesSquare, Palette, Music, Footprints, Baby, HeartHandshake, Sparkles } from "lucide-react";
import { fileUrl } from "@/lib/api";

const ICONS = { Brain, ShieldCheck, Stethoscope, Hammer, MessagesSquare, Palette, Music, Footprints, Baby, HeartHandshake };
export const CatIcon = ({ name, className = "h-4 w-4" }) => { const I = ICONS[name] || Sparkles; return <I className={className} aria-hidden />; };

export function CategoryPill({ cat, size = "sm" }) {
  if (!cat) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold text-white ${size === "sm" ? "px-2.5 py-1 text-xs" : "px-3.5 py-1.5 text-sm"}`} style={{ background: cat.color }}>
      <CatIcon name={cat.icon} className="h-3.5 w-3.5" />{cat.name}
    </span>
  );
}

export const DemoBadge = ({ show = true, className = "", label = "Exemplo fictício" }) => show ? (
  <span className={`inline-flex items-center rounded-full border border-dashed border-amber-700/60 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900 ${className}`} title={label}>{label}</span>
) : null;

export function SectionHead({ eyebrow, title, children, action, id }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-10">
      <div className="max-w-2xl">
        {eyebrow && <p className="eyebrow text-slate-500 mb-3">{eyebrow}</p>}
        <h2 id={id} className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[0.95] text-[var(--ink)]">{title}</h2>
        <span className="block h-1.5 w-40 brand-stripe mt-3" aria-hidden />
        {children && <p className="mt-4 text-slate-700 text-base md:text-lg">{children}</p>}
      </div>
      {action}
    </div>
  );
}

export function PageHero({ eyebrow, title, children, image, tone = "#15803d" }) {
  return (
    <section className="bg-white border-b" data-tone={tone} data-image={image}>
      <div className="container-x pt-10 pb-10 sm:pt-16 sm:pb-14 text-[var(--ink)]">
        {eyebrow && <p className="eyebrow text-slate-500 mb-3 rise">{eyebrow}</p>}
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.92] max-w-4xl rise" style={{ animationDelay: ".06s" }}>{title}</h1>
        <span className="block h-1.5 w-48 brand-stripe mt-4 rise" aria-hidden />
        {children && <div className="mt-6 max-w-2xl text-slate-700 text-base md:text-lg rise" style={{ animationDelay: ".12s" }}>{children}</div>}
      </div>
    </section>
  );
}

export function BrandWave({ className = "" }) {
  return (
    <svg viewBox="0 0 1440 220" preserveAspectRatio="none" className={`absolute inset-x-0 bottom-0 h-16 sm:h-24 w-full ${className}`} aria-hidden>
      <path d="M0 220 L0 170 C380 120 760 40 1440 10 L1440 220Z" fill="#1c598a" opacity=".9" />
      <path d="M560 220 C820 150 1120 110 1440 100 L1440 220Z" fill="#94b042" opacity=".95" />
    </svg>
  );
}

export function BrandMark({ className = "h-10 w-10", light = false }) {
  const c = light ? ["#fff", "#c9db50", "#fff"] : ["#5b57a0", "#1c598a", "#94b042"];
  return (
    <svg viewBox="0 0 60 64" className={className} aria-hidden>
      {[[12, 14, 20, c[0]], [30, 6, 20, c[1]], [48, 26, 26, c[2]]].map(([x, dy, len, col]) => (
        <g key={x} fill={col}><circle cx={x} cy={dy} r="5.5" /><rect x={x - 5} y={dy + 9} width="10" height={len} rx="5" /></g>
      ))}
    </svg>
  );
}

export function Prose({ text, className = "" }) {
  return (
    <div className={`space-y-4 text-slate-700 leading-relaxed ${className}`}>
      {(text || "").split(/\n\n+/).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
    </div>
  );
}

export const Img = ({ src, alt = "", className = "", position }) => src
  ? <img src={fileUrl(src)} alt={alt} loading="lazy" className={className} style={position ? { objectPosition: position } : undefined} />
  : <div className={`bg-gradient-to-br from-emerald-100 to-amber-100 ${className}`} aria-hidden />;
